import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'

import type { CardSpendingStatsPayload } from '@ankor-io/common/types/cards'

import { useUserContext } from '@/providers/user/useUserContext'

export const useCardSpendingStats = (cardId: string) => {
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()

  const [isLoading, setIsLoading] = useState(true)
  const [stats, setStats] = useState<CardSpendingStatsPayload>({
    cardId,
    last7Days: {
      total: {},
      currencies: ['USD'],
      days: {},
    },
  })

  const fetchStats = useCallback(async () => {
    if (!cardId || !activeOrganizationId || !activeConnectedAccountId) {
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)

      const res = await fetch(
        `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/card/${cardId}/spending-stats`,
        {
          headers: {
            Authorization: `Bearer ${await getAccessToken()}`,
          },
        },
      )

      if (!res.ok) {
        throw new Error(`Failed to fetch card spending stats: ${res.status}`)
      }

      const data: CardSpendingStatsPayload = await res.json()
      setStats(data)
    } catch (err) {
      console.error('Error fetching card spending stats:', err)
      toast.error('Error fetching card spending data')
    } finally {
      setIsLoading(false)
    }
  }, [cardId, activeOrganizationId, activeConnectedAccountId, getAccessToken])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  return {
    stats,
    isLoading,
    refetch: fetchStats,
  }
}
